/**
 * lib/share.js — Share-to-social helpers (Phase 2, 2026-07-23).
 *
 * Every share surface (per-entry share on /cutest-pluck, prominent
 * share block on the post-submission confirmation, future page-level
 * share on /cutest-pluck) MUST route through these builders. Keeps
 * the URL / text / brand rules in ONE place.
 *
 * Privacy contract: never accept email, user_id, address, ZIP,
 * phone, IP, caption as inputs. Only entry_id (UUID4, opaque) and
 * dog_name (public field). Enforced by companion tests.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Absolute URL to the backend-rendered OG surface for a specific
 * entry. Crawlers (facebookexternalhit, Twitterbot, LinkedInBot,
 * WhatsApp, Slackbot, TelegramBot, etc.) scrape THIS URL to build
 * the preview card. Human visitors hit it and get JS-redirected
 * to the SPA canonical `/cutest-pluck/entries/<id>?utm_source=share&utm_medium=social`.
 */
export function buildEntryShareUrl(entryId) {
  if (!entryId || typeof entryId !== "string") {
    throw new Error("buildEntryShareUrl: entryId required (string)");
  }
  return `${BACKEND_URL}/api/share/entry/${encodeURIComponent(entryId)}`;
}

/**
 * Pre-filled share text. Brand rules:
 *   - `PooPluckers` capitalized (never poopluckers or Poo Pluckers)
 *   - `Pluck` capitalized (never pluck)
 *   - Em-dash (\u2014), not hyphen, between clauses
 * Never includes email, user_id, or any PII beyond the dog_name.
 */
export function buildEntryShareText(dogName) {
  const safeName = (dogName || "our dog").trim() || "our dog";
  return (
    `My dog ${safeName} is in the Cutest Pluck on the Block contest \u2014 ` +
    `vote for us!`
  );
}

/**
 * Facebook Sharer intent URL. Facebook resolves the `u` param's
 * OG tags server-side to build the preview card — do NOT concat
 * pre-filled text into the FB URL (they ignore quote= for
 * unauthenticated apps as of 2018+).
 */
export function buildFacebookShareUrl(entryShareUrl) {
  return (
    "https://www.facebook.com/sharer/sharer.php?u=" +
    encodeURIComponent(entryShareUrl)
  );
}

/**
 * X (Twitter) intent URL. Both `text` and `url` are populated so
 * the composer opens with the copy pre-filled + the URL appended.
 */
export function buildTwitterShareUrl(entryShareUrl, dogName) {
  return (
    "https://twitter.com/intent/tweet?text=" +
    encodeURIComponent(buildEntryShareText(dogName)) +
    "&url=" +
    encodeURIComponent(entryShareUrl)
  );
}

/**
 * True when the browser exposes navigator.share — the platform-
 * native share sheet (iMessage, WhatsApp, Instagram, etc.).
 * Majority path on mobile. Fall back to explicit buttons.
 */
export function isWebShareSupported() {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function"
  );
}
